import { motion } from 'framer-motion'
import { ArrowDown, TrendingUp, Target, Zap } from 'lucide-react'

const Hero = () => {
  const features = [
    { icon: TrendingUp, title: 'AI Predictions', description: 'Advanced algorithms analyze form, fixtures, and stats' },
    { icon: Target, title: 'Accurate Forecasts', description: 'Data-driven insights for better FPL decisions' },
    { icon: Zap, title: 'Real-time Updates', description: 'Live data from official FPL and betting markets' },
  ]

  return (
    <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-fpl-green/10 rounded-full blur-3xl animate-pulse-gentle" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-fpl-blue/10 rounded-full blur-3xl animate-pulse-gentle" style={{ animationDelay: '1s' }} />
      </div>

      <div className="relative max-w-7xl mx-auto text-center">
        {/* Main Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-8"
        >
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            <span className="gradient-text">Predict</span> Your Way to
            <br />
            <span className="text-fpl-white">FPL Glory</span>
          </h1>
          <p className="text-xl text-fpl-white/80 max-w-3xl mx-auto leading-relaxed">
            Leverage AI-powered predictions, advanced statistics, and real-time data 
            to dominate your Fantasy Premier League mini-leagues
          </p>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex flex-col sm:flex-row gap-4 justify-center mb-16"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="button-primary text-lg px-8 py-4"
          >
            View Predictions
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="button-secondary text-lg px-8 py-4"
          >
            Learn More
          </motion.button>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto"
        >
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
              whileHover={{ y: -5 }}
              className="glass-effect p-6 rounded-xl text-center"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-fpl-green to-fpl-blue rounded-full flex items-center justify-center mx-auto mb-4">
                <feature.icon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-fpl-white">{feature.title}</h3>
              <p className="text-fpl-white/70">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="mt-16"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="inline-flex flex-col items-center text-fpl-white/60"
          >
            <span className="text-sm mb-2">Scroll to explore</span>
            <ArrowDown className="w-5 h-5" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

export default Hero
